import { Component, computed, effect, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { ALERT_RULE_LABELS, AlertsService } from './core/alerts.service';
import { AuthService } from './core/auth.service';
import { ConfigService } from './core/config.service';
import { MeService } from './core/me.service';

const DISMISS_KEY = 'flowie.alertBannerDismissedAt';

function readDismissed(): number {
  try {
    return Number(sessionStorage.getItem(DISMISS_KEY)) || 0;
  } catch {
    return 0;
  }
}

@Component({
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatToolbarModule,
    MatIconModule,
    MatSidenavModule,
    MatListModule,
    MatButtonModule,
    MatTooltipModule,
  ],
  selector: 'app-root',
  styleUrl: './app.scss',
  templateUrl: './app.html',
})
export class App {
  protected readonly info = inject(ConfigService).info;
  protected readonly auth = inject(AuthService);
  protected readonly me = inject(MeService);
  protected readonly alerts = inject(AlertsService);

  /** Nombre d'alertes non lues au moment où l'utilisateur a masqué le bandeau (session). */
  private readonly dismissedAt = signal(readDismissed());
  /** Détail du bandeau, ex. « Factures rejetées : 2 · Échecs : 1 ». */
  protected readonly bannerDetail = computed(() =>
    (this.alerts.summary()?.byRule ?? [])
      .filter((r) => r.unread > 0)
      .map((r) => `${ALERT_RULE_LABELS[r.rule]} : ${r.unread}`)
      .join(' · '),
  );
  /** Le bandeau réapparaît si de nouvelles alertes arrivent après l'avoir masqué. */
  protected readonly showAlertBanner = computed(() => {
    const unread = this.alerts.summary()?.unread ?? 0;
    return unread > 0 && unread > this.dismissedAt();
  });

  constructor() {
    // Compteurs d'alertes dès que l'utilisateur est connu.
    effect(() => {
      if (this.me.me()) this.alerts.start();
    });
  }

  protected dismissBanner(): void {
    const unread = this.alerts.summary()?.unread ?? 0;
    this.dismissedAt.set(unread);
    try {
      sessionStorage.setItem(DISMISS_KEY, String(unread));
    } catch {
      // Stockage indisponible (navigation privée…) : masqué pour la page courante seulement.
    }
  }

  protected logout(): void {
    void this.auth.logout();
  }
}
